import { DeleteOutlineRounded } from "@mui/icons-material";
import { Button, Divider, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import {
  ActionItemContextProperty,
  ActionItemFeed,
  ActionItemProperties,
  ItemDescriptionField,
  ShareButton,
  SignalRGroup,
} from "../../components";
import { ActionItemPropertyType, Links } from "../../constants";
import { useSignalR } from "../../hooks";
import { queryKeys, useDeleteActionItem, useEditActionItem, useGetActionItemDetail } from "../../http";
import { setTitlePrefix, useDestructiveConfirm } from "../../utilities";
import { DetailPageLayout } from "./DetailPageLayout";

interface UrlParams {
  actionItemGuid: string;
}

const ActionItemDetailsPage = () => {
  const { actionItemGuid } = useParams<keyof UrlParams>() as UrlParams;
  const { isLoading, data: actionItem } = useGetActionItemDetail(actionItemGuid);
  const { mutate: editActionItem } = useEditActionItem();
  const { mutate: deleteActionItem } = useDeleteActionItem();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const confirm = useDestructiveConfirm();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.ActionItem(actionItemGuid));
    return () => signalR.leaveGroup(SignalRGroup.ActionItem(actionItemGuid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionItemGuid]);

  useEffect(() => {
    const handleShouldRefetchActionItem = (actionItemGuid: string) => {
      queryClient.invalidateQueries(queryKeys.actionItems.detail(actionItemGuid));
    };

    signalR.onShouldRefetchActionItem(handleShouldRefetchActionItem);
    return () => signalR.offShouldRefetchActionItem(handleShouldRefetchActionItem);
  }, [queryClient, signalR]);

  useEffect(() => {
    setTitlePrefix(actionItem?.description);
  }, [actionItem?.description]);

  function handleDescriptionChanged(value: string) {
    if (actionItem === undefined) {
      return;
    }

    editActionItem({
      actionItemGuid: actionItem.guid,
      description: value,
      planGuid: actionItem.teamPlanSection.planGuid,
    });
  }

  async function handleDeleteClicked() {
    if (actionItem === undefined) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Delete Item",
      content: "Are you sure you want to delete this item?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteActionItem(
      {
        actionItemGuid: actionItemGuid,
        planGuid: actionItem.teamPlanSection.planGuid,
      },
      {
        onSuccess: () => {
          navigate(Links.TeamStrategicMap(actionItem.teamPlanSection.teamSlug));
          enqueueSnackbar(`Item has been deleted`);
        },
      },
    );
  }

  return (
    <DetailPageLayout
      title="Strategic Item"
      actions={
        <Stack direction="row" spacing={1}>
          <ShareButton variant="outlined" href={window.location.href} />
          <Divider orientation="vertical" />
          {actionItem?.userCanEdit && (
            <Button variant="outlined" color="error" onClick={handleDeleteClicked} sx={{ minWidth: 18 }}>
              <DeleteOutlineRounded />
            </Button>
          )}
        </Stack>
      }
    >
      <Stack divider={<Divider />}>
        <Stack sx={{ px: { xs: 0.5, sm: 1 } }} divider={<Divider flexItem variant="dashed" />}>
          {!isLoading && (actionItem?.context || actionItem?.userCanEdit) && (
            <ActionItemContextProperty actionItem={actionItem} isEditable={actionItem.userCanEdit} sx={{ py: 0.25 }} />
          )}

          <ItemDescriptionField
            isLoading={isLoading}
            isEditable={actionItem?.userCanEdit ?? false}
            value={actionItem?.description}
            onChange={handleDescriptionChanged}
            variant="h4"
            sx={{ py: 0.5 }}
          />
        </Stack>

        <ActionItemProperties
          isLoading={isLoading}
          actionItem={actionItem}
          propertyVisibility={{
            [ActionItemPropertyType.Notes]: true,
            [ActionItemPropertyType.Ownership]: true,
            [ActionItemPropertyType.Status]: true,
            [ActionItemPropertyType.DueDate]: true,
            [ActionItemPropertyType.SupportingItems]: actionItem?.teamPlanSection != null,
            [ActionItemPropertyType.Metrics]: true,
            [ActionItemPropertyType.AzureDevOps]: true,
          }}
        />

        <ActionItemFeed actionItemGuid={actionItemGuid} />
      </Stack>
    </DetailPageLayout>
  );
};

export { ActionItemDetailsPage };
